import React from "react";
import TapCard from "./TapCard";

const PROJECT_TYPES = {
  Painting: [
    { emoji: "🏠", label: "Inside my home" },
    { emoji: "🏡", label: "Outside my home" },
    { emoji: "🏘️", label: "Rental property" },
    { emoji: "🖼️", label: "Feature wall" },
    { emoji: "🌿", label: "Fence or deck" },
    { emoji: "🔧", label: "Touch-ups" },
  ],
  Plumbing: [
    { emoji: "💧", label: "Something is leaking" },
    { emoji: "🚿", label: "Blocked drain" },
    { emoji: "🔥", label: "Hot water problem" },
    { emoji: "🚽", label: "Toilet issue" },
    { emoji: "🔩", label: "New tap or fixture" },
    { emoji: "🔧", label: "Bathroom renovation" },
    { emoji: "🔥", label: "Gas work" },
  ],
  Electrical: [
    { emoji: "💡", label: "Install new lights" },
    { emoji: "🔌", label: "Power points" },
    { emoji: "⚡", label: "Fault or no power" },
    { emoji: "🔒", label: "Safety check" },
    { emoji: "🏠", label: "Switchboard" },
    { emoji: "🔧", label: "Renovation wiring" },
    { emoji: "🚨", label: "Smoke alarms" },
  ],
  "Solar & Battery": [
    { emoji: "☀️", label: "New solar system" },
    { emoji: "🔋", label: "Battery storage" },
    { emoji: "📦", label: "Solar + battery package" },
    { emoji: "⬆️", label: "System upgrade" },
    { emoji: "🔧", label: "Inverter replacement" },
    { emoji: "🔍", label: "Solar health check" },
  ],
  Building: [
    { emoji: "🏠", label: "New build" },
    { emoji: "🏗️", label: "Home extension" },
    { emoji: "🔨", label: "Renovation" },
    { emoji: "🏚️", label: "Garage or shed" },
    { emoji: "🌳", label: "Deck or pergola" },
    { emoji: "🔄", label: "Knockdown rebuild" },
  ],
  Carpentry: [
    { emoji: "🚪", label: "Doors" },
    { emoji: "📐", label: "Skirting boards" },
    { emoji: "🌳", label: "Decking" },
    { emoji: "🏗️", label: "Framing" },
    { emoji: "🗄️", label: "Shelving or cabinets" },
    { emoji: "🔧", label: "General repairs" },
  ],
  Landscaping: [
    { emoji: "🌿", label: "Garden clean-up" },
    { emoji: "🌱", label: "Lay new turf" },
    { emoji: "🧱", label: "Retaining wall" },
    { emoji: "🪨", label: "Paving" },
    { emoji: "🌸", label: "Planting" },
    { emoji: "🏡", label: "Full yard makeover" },
  ],
  Roofing: [
    { emoji: "💧", label: "Roof is leaking" },
    { emoji: "🍂", label: "Gutters" },
    { emoji: "🏠", label: "Roof restoration" },
    { emoji: "🔧", label: "Tile repairs" },
    { emoji: "⚙️", label: "Metal roof" },
    { emoji: "🔧", label: "Repointing" },
  ],
  Cleaning: [
    { emoji: "🏠", label: "End of lease" },
    { emoji: "🧹", label: "Deep clean" },
    { emoji: "📅", label: "Regular clean" },
    { emoji: "🏢", label: "Office" },
    { emoji: "🔨", label: "Post-renovation" },
    { emoji: "🪟", label: "Windows" },
  ],
  Concreter: [
    { emoji: "🚗", label: "Driveway" },
    { emoji: "🏗️", label: "Slab or foundation" },
    { emoji: "🚶", label: "Paths & pathways" },
    { emoji: "🏊", label: "Pool surrounds" },
    { emoji: "✨", label: "Decorative concrete" },
    { emoji: "🔧", label: "Repairs" },
  ],
  Handyman: [
    { emoji: "🔧", label: "General repairs" },
    { emoji: "📺", label: "Wall mounting" },
    { emoji: "🚪", label: "Door or lock" },
    { emoji: "📦", label: "Flat pack" },
    { emoji: "🪣", label: "Patch and paint" },
    { emoji: "✅", label: "Odd jobs" },
  ],
  HVAC: [
    { emoji: "❄️", label: "New air conditioner" },
    { emoji: "🌬️", label: "Ducted system" },
    { emoji: "🔧", label: "Service & maintenance" },
    { emoji: "🛠️", label: "Repair" },
    { emoji: "🔄", label: "Replacement unit" },
    { emoji: "💨", label: "Evaporative cooling" },
  ],
};

const OTHER_OPTION = { emoji: "✏️", label: "Other (not on list)" };

export default function StepProjectType({ form, update, onNext }) {
  const options = PROJECT_TYPES[form.trade];
  // Custom-typed value mode: when the user picks "Other" we hold the value
  // locally and only commit on submit so we don't bounce them out of the
  // input every keystroke.
  const [otherText, setOtherText] = React.useState(
    options && form.projectType && !options.some((o) => o.label === form.projectType)
      ? form.projectType
      : ""
  );
  const [otherActive, setOtherActive] = React.useState(
    !!options && !!form.projectType && !options.some((o) => o.label === form.projectType)
  );

  function handleSelect(label) {
    update("projectType", label);
    // Bug 1 fix: pass value directly so parent validateStep doesn't rely on stale form state
    setTimeout(() => onNext(label), 150);
  }

  function commitOther() {
    const trimmed = otherText.trim();
    if (!trimmed) return;
    update("projectType", trimmed);
    onNext(trimmed);
  }

  // Bug 12 fix: fallback has proper heading and Next button
  if (!options) {
    return (
      <div>
        <h2 style={{ margin: "0 0 4px", fontSize: "1.3rem", fontWeight: 700, color: "var(--text, #1C1C1E)" }}>
          What kind of job is it?
        </h2>
        <p style={{ margin: "0 0 20px", fontSize: "0.9rem", color: "var(--muted, #6b7280)" }}>
          Briefly describe the type of work needed
        </p>
        <input
          type="text"
          value={form.projectType}
          onChange={(e) => update("projectType", e.target.value)}
          placeholder="What kind of job is it?"
          style={{ width: "100%", fontSize: "1rem", padding: "14px", borderRadius: "12px", border: "2px solid var(--border, #e5e7eb)", boxSizing: "border-box" }}
        />
        <div style={{ marginTop: "20px" }}>
          <button
            type="button"
            onClick={() => onNext()}
            style={{
              background: "var(--primary)",
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              padding: "16px 32px",
              fontSize: "1rem",
              fontWeight: 700,
              cursor: "pointer",
              width: "100%",
            }}
          >
            Next →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>{`
        @media (min-width: 600px) {
          .step-project-type-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
      `}</style>
      <div
        className="step-project-type-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "12px",
        }}
      >
        {options.map((opt, i) => (
          <TapCard
            key={`${opt.label}-${i}`}
            emoji={opt.emoji}
            label={opt.label}
            selected={!otherActive && form.projectType === opt.label}
            onSelect={() => {
              setOtherActive(false);
              handleSelect(opt.label);
            }}
          />
        ))}
        <TapCard
          key="other"
          emoji={OTHER_OPTION.emoji}
          label={OTHER_OPTION.label}
          selected={otherActive}
          onSelect={() => setOtherActive(true)}
        />
      </div>

      {otherActive && (
        <div style={{ marginTop: "16px" }}>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: "6px",
              fontSize: "0.95rem",
              color: "var(--text, #1C1C1E)",
            }}
          >
            Tell us what kind of job
          </label>
          <input
            type="text"
            autoFocus
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitOther();
              }
            }}
            placeholder="e.g. Solar panel cleaning"
            style={{
              width: "100%",
              fontSize: "1rem",
              padding: "14px",
              borderRadius: "12px",
              border: "2px solid var(--border, #e5e7eb)",
              boxSizing: "border-box",
            }}
          />
          <button
            type="button"
            onClick={commitOther}
            disabled={!otherText.trim()}
            style={{
              marginTop: "12px",
              background: "var(--primary)",
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              padding: "16px 32px",
              fontSize: "1rem",
              fontWeight: 700,
              cursor: otherText.trim() ? "pointer" : "not-allowed",
              opacity: otherText.trim() ? 1 : 0.5,
              width: "100%",
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
